import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import { DialogProps } from '@mui/material';
import { ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from 'assets/icons/containerActions/trash.svg';
import {
  BaseTextFieldController,
  CountryCodeSelect,
  Form,
  FormGroup,
  SelectController,
  FullPageProgress,
  NewConfirmDialog,
} from 'components';
import { countries, FIELDS_VALUE_LENGTH } from 'constants/index';
import { PhoneInputW } from 'features/employeeDetails/components/EditEmployeeForm/styled';
import { useModal, useWindowSize } from 'hooks';
import { textFieldMobileStyles, textFieldStyles } from 'styles';
import { Company, CustomField } from 'types';
import { stripNonDigits } from 'utils';
import { required } from 'validations';

import { EDIT_FORM_FIELDS_LENGTH } from './constants';
import { useEditCompanyForm } from './hooks';
import { emailFieldRules, phoneRules } from './rules';
import {
  CancelBtn,
  CreateBtn,
  DeleteIconContainer,
  DialogTitle,
  Footer,
  FormButtonsWrapper,
  GroupTitle,
  ModalContent,
  ModalOverlay,
} from './styled';
import { countryCodeInputStyles, countryCodeInputMobileStyles } from './stylesConfig';

interface EditCompanyFormProps extends DialogProps {
  onClose: () => void;
  company: Company;
}

export function EditCompanyForm({ onClose, company }: EditCompanyFormProps): ReactElement {
  const { t } = useTranslation();
  const {
    control,
    handleFormSubmit,
    isLoading,
    isOpenConfirmModal,
    handleTitleClick,
    handleCloseForm,
    handleCloseConfirmModal,
    addGeneralField,
    addContactField,
    newGeneralFields,
    newContactFields,
    setNewGeneralFields,
    setNewContactFields,
    removeGeneralField,
    removeContactField,
    isGeneralAddDisabled,
    isContactAddDisabled,
  } = useEditCompanyForm({ onClose, company });
  const { handleOpenModal, handleCloseModal, isOpen } = useModal();

  const { isMobile } = useWindowSize();
  const deleteFieldData = useRef<{ fieldId: string; isNewField?: boolean; index?: number } | null>(
    null,
  );
  const deleteFieldType = useRef('');
  const formGroupMarginBottom = isMobile ? 8 : 24;
  const fieldStyles = isMobile ? textFieldMobileStyles : textFieldStyles;

  const handleNewGeneralFieldChange = (index: number, field: 'field' | 'value', value: string) => {
    const updatedFields = [...newGeneralFields];
    updatedFields[index] = { ...updatedFields[index], [field]: value };
    setNewGeneralFields(updatedFields);
  };

  const handleNewContactFieldChange = (index: number, field: 'field' | 'value', value: string) => {
    const updatedFields = [...newContactFields];
    updatedFields[index] = { ...updatedFields[index], [field]: value };
    setNewContactFields(updatedFields);
  };

  const handleDeleteTarget = () => {
    if (deleteFieldData.current?.fieldId || deleteFieldData.current?.isNewField) {
      if (deleteFieldType.current === 'general') {
        removeGeneralField(
          deleteFieldData.current.fieldId,
          deleteFieldData.current.isNewField,
          deleteFieldData.current.index,
        );
      } else {
        removeContactField(
          deleteFieldData.current.fieldId,
          deleteFieldData.current.isNewField,
          deleteFieldData.current.index,
        );
      }
      handleCloseModal();
    }
  };

  const handleFieldDelete = (
    type = 'general',
    fieldId = '',
    isNewField?: boolean,
    index?: number,
  ) => {
    deleteFieldData.current = { fieldId, isNewField, index };
    deleteFieldType.current = type;
    handleOpenModal();
  };

  return (
    <>
      <ModalOverlay>
        {isMobile && (
          <DialogTitle onClick={handleTitleClick}>
            <KeyboardBackspaceRoundedIcon fontSize="large" />
            {t('company.edit.form.title')}
          </DialogTitle>
        )}
        <ModalContent>
          {!isMobile && (
            <DialogTitle onClick={handleTitleClick}>
              <KeyboardBackspaceRoundedIcon fontSize="large" />
              {t('company.edit.form.title')}
            </DialogTitle>
          )}

          <Form onSubmit={handleFormSubmit}>
            <GroupTitle>{t('company.edit.form.general.title')}</GroupTitle>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <BaseTextFieldController
                name="name"
                control={control}
                placeholder={t('company.edit.form.general.placeholders.name')}
                sx={fieldStyles}
                maxLength={EDIT_FORM_FIELDS_LENGTH.name.max}
                rules={required}
              />
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <BaseTextFieldController
                name="industry"
                control={control}
                placeholder={t('company.edit.form.general.placeholders.industry')}
                sx={fieldStyles}
                maxLength={EDIT_FORM_FIELDS_LENGTH.industry.max}
                rules={required}
              />
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <BaseTextFieldController
                name="vat"
                control={control}
                placeholder={t('company.edit.form.general.placeholders.vatNumber')}
                sx={fieldStyles}
                maxLength={EDIT_FORM_FIELDS_LENGTH.vatNumber.max}
              />
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <BaseTextFieldController
                name="company_number"
                control={control}
                placeholder={t('company.edit.form.general.placeholders.companyNumber')}
                sx={fieldStyles}
                maxLength={EDIT_FORM_FIELDS_LENGTH.companyNumber.max}
                rules={required}
              />
            </FormGroup>
            {/* Render existing general custom fields */}
            {control._formValues.custom_fields?.general.length
              ? control._formValues.custom_fields.general.map(
                  (customField: CustomField, index: number) => {
                    const { field, value, order } = customField;

                    if (typeof value !== 'string' || !field || !order) {
                      return;
                    }

                    return (
                      <FormGroup
                        key={`general-existing-${index}`}
                        marginBottom={formGroupMarginBottom}
                        row
                        sx={{ columnGap: isMobile ? '8px' : '20px' }}
                      >
                        <BaseTextFieldController
                          name={`custom_fields.general.${index}.value`}
                          placeholder={field}
                          control={control}
                          sx={{ ...fieldStyles, flex: 1 }}
                          maxLength={EDIT_FORM_FIELDS_LENGTH.companyNumber.max}
                        />
                        <DeleteIconContainer>
                          <DeleteIcon
                            onClick={() => handleFieldDelete('general', field)}
                            style={{ opacity: '0.4', fontSize: 40 }}
                          />
                        </DeleteIconContainer>
                      </FormGroup>
                    );
                  },
                )
              : null}
            {newGeneralFields.map((customField, index) => {
              return (
                <FormGroup
                  key={`general-new-${index}`}
                  marginBottom={formGroupMarginBottom}
                  row
                  sx={{ columnGap: isMobile ? '8px' : '20px' }}
                >
                  <BaseTextFieldController
                    name={`custom_fields.general.${customField.order}.field`}
                    control={control}
                    placeholder={t('company.edit.form.general.placeholders.fieldLabel')}
                    value={customField.field}
                    onChange={(e) => handleNewGeneralFieldChange(index, 'field', e.target.value)}
                    sx={{ ...fieldStyles, flex: 1 }}
                    maxLength={50}
                    rules={required}
                  />
                  <BaseTextFieldController
                    name={`custom_fields.general.${customField.order}.value`}
                    control={control}
                    placeholder={t('company.edit.form.general.placeholders.fieldValue')}
                    value={customField.value}
                    onChange={(e) => handleNewGeneralFieldChange(index, 'value', e.target.value)}
                    sx={{ ...fieldStyles, flex: 2 }}
                    maxLength={100}
                  />
                  <DeleteIconContainer>
                    <DeleteIcon
                      onClick={() => handleFieldDelete('general', '', true, index)}
                      style={{ opacity: '0.4', fontSize: 40 }}
                    />
                  </DeleteIconContainer>
                </FormGroup>
              );
            })}
            <FormGroup
              marginBottom={formGroupMarginBottom}
              style={{
                display: 'flex',
                height: '56px',
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              <CreateBtn
                variant="contained"
                disabled={isGeneralAddDisabled}
                onClick={addGeneralField}
              >
                + {t('company.edit.form.addField')}
              </CreateBtn>
            </FormGroup>

            <GroupTitle sx={{ marginTop: isMobile ? '8px' : 0 }}>
              {t('company.edit.form.contactInfo.title')}
            </GroupTitle>

            <FormGroup marginBottom={formGroupMarginBottom}>
              <BaseTextFieldController
                name="address_1"
                control={control}
                placeholder={t('company.edit.form.contactInfo.placeholders.address1')}
                sx={fieldStyles}
                maxLength={EDIT_FORM_FIELDS_LENGTH.address1.max}
              />
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <BaseTextFieldController
                name="address_2"
                control={control}
                placeholder={t('company.edit.form.contactInfo.placeholders.address2')}
                sx={fieldStyles}
                maxLength={EDIT_FORM_FIELDS_LENGTH.address2.max}
              />
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <BaseTextFieldController
                name="city"
                control={control}
                placeholder={t('company.edit.form.contactInfo.placeholders.city')}
                sx={fieldStyles}
                maxLength={EDIT_FORM_FIELDS_LENGTH.city.max}
              />
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <BaseTextFieldController
                name="region"
                control={control}
                placeholder={t('company.edit.form.contactInfo.placeholders.state')}
                sx={fieldStyles}
                maxLength={EDIT_FORM_FIELDS_LENGTH.province.max}
              />
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <BaseTextFieldController
                name="postcode"
                control={control}
                placeholder={t('company.edit.form.contactInfo.placeholders.postcode')}
                sx={fieldStyles}
                maxLength={EDIT_FORM_FIELDS_LENGTH.postCode.max}
              />
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <SelectController
                name="country"
                control={control}
                placeholder={t('company.edit.form.contactInfo.placeholders.country')}
                options={countries}
                withDefaultEmptyOption
                sx={{ borderRadius: '8px' }}
              />
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <BaseTextFieldController
                name="email"
                control={control}
                placeholder={t('company.edit.form.contactInfo.placeholders.companyEmail')}
                sx={fieldStyles}
                maxLength={EDIT_FORM_FIELDS_LENGTH.companyEmail.max}
                rules={emailFieldRules}
              />
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <BaseTextFieldController
                name="support_email"
                control={control}
                placeholder={t('company.edit.form.contactInfo.placeholders.supportEmail')}
                sx={fieldStyles}
                maxLength={EDIT_FORM_FIELDS_LENGTH.supportEmail.max}
                rules={emailFieldRules}
              />
            </FormGroup>
            <FormGroup
              marginBottom={formGroupMarginBottom}
              row
              sx={{ columnGap: isMobile ? '8px' : '20px' }}
            >
              <CountryCodeSelect
                control={control}
                name="country_code"
                containerStyle={{
                  width: isMobile ? '105px' : '170px',
                  height: isMobile ? '54px' : '58px',
                }}
                inputStyle={isMobile ? countryCodeInputMobileStyles : countryCodeInputStyles}
                buttonStyle={{
                  left: '10px',
                }}
              />
              <PhoneInputW>
                <BaseTextFieldController
                  name="phone"
                  control={control}
                  placeholder={t('company.edit.form.contactInfo.placeholders.phone')}
                  sx={{ flex: 1, ...fieldStyles }}
                  format={stripNonDigits}
                  maxLength={FIELDS_VALUE_LENGTH.phone.max}
                  rules={phoneRules}
                />
              </PhoneInputW>
            </FormGroup>
            <FormGroup marginBottom={formGroupMarginBottom}>
              <BaseTextFieldController
                name="website"
                control={control}
                placeholder={t('company.edit.form.contactInfo.placeholders.website')}
                sx={fieldStyles}
                maxLength={EDIT_FORM_FIELDS_LENGTH.website.max}
              />
            </FormGroup>
            {/* Render existing contact custom fields */}
            {control._formValues.custom_fields?.contact.length
              ? control._formValues.custom_fields.contact.map(
                  (customField: CustomField, index: number) => {
                    const { field, value, order } = customField;

                    if (typeof value !== 'string' || !field || !order) {
                      return;
                    }

                    return (
                      <FormGroup
                        key={`contact-existing-${index}`}
                        marginBottom={formGroupMarginBottom}
                        row
                        sx={{ columnGap: isMobile ? '8px' : '20px' }}
                      >
                        <BaseTextFieldController
                          name={`custom_fields.contact.${index}.value`}
                          placeholder={field}
                          control={control}
                          sx={{ ...fieldStyles, flex: 1 }}
                          maxLength={EDIT_FORM_FIELDS_LENGTH.companyNumber.max}
                        />
                        <DeleteIconContainer>
                          <DeleteIcon
                            onClick={() => handleFieldDelete('contact', field)}
                            style={{ opacity: '0.4', fontSize: 40 }}
                          />
                        </DeleteIconContainer>
                      </FormGroup>
                    );
                  },
                )
              : null}
            {/* Render new contact fields */}
            {newContactFields.map((customField, index) => (
              <FormGroup
                key={`contact-new-${index}`}
                marginBottom={formGroupMarginBottom}
                row
                sx={{ columnGap: isMobile ? '8px' : '20px' }}
              >
                <BaseTextFieldController
                  name={`custom_fields.contact.${customField.order}.field`}
                  control={control}
                  placeholder={t('company.edit.form.contactInfo.placeholders.fieldLabel')}
                  value={customField.field}
                  onChange={(e) => handleNewContactFieldChange(index, 'field', e.target.value)}
                  sx={{ ...fieldStyles, flex: 1 }}
                  maxLength={50}
                  rules={required}
                />
                <BaseTextFieldController
                  name={`custom_fields.contact.${customField.order}.value`}
                  control={control}
                  placeholder={t('company.edit.form.contactInfo.placeholders.fieldValue')}
                  value={customField.value}
                  onChange={(e) => handleNewContactFieldChange(index, 'value', e.target.value)}
                  sx={{ ...fieldStyles, flex: 2 }}
                  maxLength={100}
                />
                <DeleteIconContainer>
                  <DeleteIcon
                    onClick={() => handleFieldDelete('contact', '', true, index)}
                    style={{ opacity: '0.4', fontSize: 40 }}
                  />
                </DeleteIconContainer>
              </FormGroup>
            ))}
            <FormGroup
              marginBottom={formGroupMarginBottom}
              style={{
                display: 'flxe',
                flexDirection: 'row',
                height: '56px',
                justifyContent: 'center',
              }}
            >
              <CreateBtn
                variant="contained"
                disabled={isContactAddDisabled}
                onClick={addContactField}
              >
                + {t('company.edit.form.addField')}
              </CreateBtn>
            </FormGroup>

            <Footer>
              <FormButtonsWrapper>
                <CancelBtn variant="outlined" onClick={onClose}>
                  {t('document.create.form.buttons.cancel')}
                </CancelBtn>
                <CreateBtn variant="contained" onClick={handleFormSubmit} disabled={isLoading}>
                  {t('document.create.form.buttons.edit')}
                </CreateBtn>
              </FormButtonsWrapper>
            </Footer>
          </Form>
        </ModalContent>
      </ModalOverlay>

      {isOpenConfirmModal && (
        <NewConfirmDialog
          title={t('company.edit.modal.title')}
          subTitle={t('company.edit.modal.exit')}
          open={isOpenConfirmModal}
          onClose={handleCloseConfirmModal}
          onConfirm={handleCloseForm}
          submitLabel={t('company.edit.modal.successButton')}
          cancelLabel={t('company.edit.modal.cancelButton')}
        />
      )}
      {isOpen && (
        <NewConfirmDialog
          title={t('company.edit.removeModal.title', {
            fieldLabel: deleteFieldData.current?.fieldId,
          })}
          subTitle={t('company.edit.removeModal.subtitle')}
          open={isOpen}
          onConfirm={handleDeleteTarget}
          onClose={handleCloseModal}
          submitLabel={t('company.edit.removeModal.successButton')}
          cancelLabel={t('company.edit.removeModal.cancelButton')}
        />
      )}
      {isLoading && <FullPageProgress />}
    </>
  );
}
